import { useCallback, useMemo } from 'react';
import type {
  EnvironmentKey,
  AuthoringEnvironmentClientInfo,
  BaseAuthoringEnvironmentConfig,
  PreviewAuthoringEnvironment,
  Nullable,
} from '@web-config-app/core';
import { useConfigApp } from '../use-config-app/use-config-app';

type EnvironmentMap = Map<EnvironmentKey, AuthoringEnvironmentClientInfo>;

/**
 * Ensures that re always get a boolean value for properties that are optional booleans in the
 * configuration but required booleans in {@link AuthoringEnvironmentClientInfo}
 */

export const getBooleanValueOrDefault = (
  value: boolean | undefined,
  defaultValue: boolean,
): boolean => {
  if (typeof value !== 'undefined') return value;

  return defaultValue;
};

/**
 * Since {@link BaseAuthoringEnvironmentConfig} allows for some properties to be optional AND also
 * omits the `type` (which we always want to be equal to `base`), we want to pass the config through this
 * function and add values for the omitted and optional properties
 */

export const getBaseEnvironmentWithDefaults = ({
  publishDangerously,
  defaultAuthoringEnvironment,
  ...baseEnvironment
}: BaseAuthoringEnvironmentConfig): AuthoringEnvironmentClientInfo => ({
  ...baseEnvironment,
  type: 'base',
  defaultAuthoringEnvironment: getBooleanValueOrDefault(
    defaultAuthoringEnvironment,
    false,
  ),
  publishDangerously,
});

export const getPreviewEnvironmentWithDefaults = ({
  defaultAuthoringEnvironment,
  publishDangerously,
  ...authoringEnv
}: PreviewAuthoringEnvironment): AuthoringEnvironmentClientInfo => ({
  ...authoringEnv,
  publishDangerously,
  // Carried through as declared, not decided here: the default is a property of the whole
  // set, so resolveDefaultAuthoringEnvironment owns it and the Map stamps the answer on.
  defaultAuthoringEnvironment: getBooleanValueOrDefault(
    defaultAuthoringEnvironment,
    false,
  ),
});

/**
 * Picks the environment an author lands in by default. Sole owner of that rule.
 *
 * An explicit flag wins; failing that a lone preview environment is implicitly the default.
 * With several previews and no flag the answer is genuinely ambiguous, so it falls back to
 * base rather than to whichever the Map yields first -- and
 * `validateMultiPreviewTenantsDeclareADefault` stops a tenant reaching that state at all.
 */
const resolveDefaultAuthoringEnvironment = (
  baseEnvironment: AuthoringEnvironmentClientInfo,
  previewEnvironments: AuthoringEnvironmentClientInfo[],
): AuthoringEnvironmentClientInfo => {
  if (baseEnvironment.defaultAuthoringEnvironment) return baseEnvironment;

  const explicitlyFlagged = previewEnvironments.find(
    (env) => env.defaultAuthoringEnvironment,
  );
  if (explicitlyFlagged) return explicitlyFlagged;

  return previewEnvironments.length === 1
    ? previewEnvironments[0]
    : baseEnvironment;
};

export const getCombinedAuthoringEnvironmentsMap = (
  baseEnvironment: AuthoringEnvironmentClientInfo,
  previewEnvironments: PreviewAuthoringEnvironment[] = [],
) => {
  const previewEnvironmentClientInfos = previewEnvironments.map(
    getPreviewEnvironmentWithDefaults,
  );

  const defaultEnvironment = resolveDefaultAuthoringEnvironment(
    baseEnvironment,
    previewEnvironmentClientInfos,
  );

  return new Map<EnvironmentKey, AuthoringEnvironmentClientInfo>(
    [baseEnvironment, ...previewEnvironmentClientInfos].map((env) => [
      env.environmentKey,
      // Stamped from the resolved winner rather than patched in afterwards, which put the
      // rule in two functions that could disagree.
      { ...env, defaultAuthoringEnvironment: env === defaultEnvironment },
    ]),
  );
};
export interface UseAuthoringEnvironmentsResult {
  /**
   * All app configs have a base environment
   */
  baseAuthoringEnvironment: AuthoringEnvironmentClientInfo;
  /**
   * The default environment is configurable but defaults to:
   *
   * 1. in a multi-environment setup, it defaults to the `preview` type
   * 2. in a single environment, it defaults to `base`
   */
  defaultAuthoringEnvironment: AuthoringEnvironmentClientInfo;
  /**
   * The ID of the current authoring environment
   */
  currentAuthoringEnvironment: EnvironmentKey;
  /**
   * The non-current authoring environment, if there is one -- the first of them when there
   * are several. Kept for existing single-target consumers.
   */
  otherAuthoringEnvironment?: EnvironmentKey;
  /**
   * Every authoring environment other than the current one. 0 or 1 for the common case; 2+
   * once a tenant has several preview environments, at which point "the other one" is
   * ambiguous and a consumer needs the full list to offer a target per environment.
   */
  otherAuthoringEnvironments: EnvironmentKey[];
  multiEnvironmentAuthoringEnabled: boolean;
  environmentMap: Nullable<EnvironmentMap>;
  /**
   * What an environment is called in the UI, for a caller holding only its key. Falls back to
   * the key itself for one this tenant doesn't configure.
   */
  getEnvironmentLabel: (environmentKey: EnvironmentKey) => string;
}

export const useAuthoringEnvironments = ({
  environmentKey,
}: {
  environmentKey?: EnvironmentKey;
} = {}): UseAuthoringEnvironmentsResult => {
  const {
    authoring: {
      baseEnvironment: baseEnvironmentConfig,
      previewEnvironments = [],
      disableMultiEnvironmentOperations,
    },
  } = useConfigApp();
  const baseAuthoringEnvironment = useMemo(
    () => getBaseEnvironmentWithDefaults(baseEnvironmentConfig),
    [baseEnvironmentConfig],
  );

  const multiEnvironmentAuthoringEnabled =
    disableMultiEnvironmentOperations !== true &&
    previewEnvironments.length > 0;

  const environmentMap = useMemo<Nullable<EnvironmentMap>>(
    () =>
      multiEnvironmentAuthoringEnabled
        ? getCombinedAuthoringEnvironmentsMap(
            baseAuthoringEnvironment,
            previewEnvironments,
          )
        : null,
    [
      baseAuthoringEnvironment,
      previewEnvironments,
      multiEnvironmentAuthoringEnabled,
    ],
  );

  // Reads the flag the map already resolved rather than re-deciding it, so there's one
  // answer to "which environment is the default" instead of two that can drift.
  const defaultAuthoringEnvironment =
    useMemo<AuthoringEnvironmentClientInfo>(() => {
      // The map is null exactly when multi-environment authoring is off, so asking both
      // would be the same question twice.
      if (environmentMap === null) {
        return baseAuthoringEnvironment;
      }
      return (
        Array.from(environmentMap.values()).find(
          (env) => env.defaultAuthoringEnvironment,
        ) ?? baseAuthoringEnvironment
      );
    }, [baseAuthoringEnvironment, environmentMap]);

  const currentAuthoringEnvironment =
    environmentKey ?? baseAuthoringEnvironment.environmentKey;

  const otherAuthoringEnvironments = useMemo(
    () =>
      environmentMap
        ? Array.from(environmentMap.keys()).filter(
            (key: EnvironmentKey) => key !== currentAuthoringEnvironment,
          )
        : [],
    [environmentMap, currentAuthoringEnvironment],
  );

  const otherAuthoringEnvironment = otherAuthoringEnvironments[0];

  // The map is null with multi-environment authoring off, which leaves only the base to name.
  const getEnvironmentLabel = useCallback(
    (key: EnvironmentKey) =>
      environmentMap?.get(key)?.label ??
      (key === baseAuthoringEnvironment.environmentKey
        ? baseAuthoringEnvironment.label
        : key),
    [environmentMap, baseAuthoringEnvironment],
  );

  return {
    baseAuthoringEnvironment,
    defaultAuthoringEnvironment,
    currentAuthoringEnvironment,
    multiEnvironmentAuthoringEnabled,
    environmentMap,
    otherAuthoringEnvironment,
    otherAuthoringEnvironments,
    getEnvironmentLabel,
  };
};
